import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Users, Bell, PlusCircle
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  useSession, 
  useCategories, 
  useDiscussions, 
  usePopularDiscussions,
  useDiscussionDetail,
  useCreateDiscussion,
  useAddComment,
  useToggleLike,
  useToggleBookmark,
  ViewType,
  DiscussionQueryParams,
  errNoUsername,
  useUpdateDiscussion
} from '../queries/community';
import { Routes, Route, Link, useParams, useNavigate, Outlet } from 'react-router-dom';

// Import separated components
import NewDiscussionModal from './Community/NewDiscussionModal';
import SubscribeModal from './Community/SubscribeModal';
import VerificationModal from './Community/VerificationModal';
import LoginModal from './Community/LoginModal';
import UsernameScreen from './Community/UsernameScreen';
import GuidelinesView from './Community/GuidelinesView';
import DiscussionDetailView from './Community/DiscussionDetailView';
import MainView from './Community/MainView';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';

interface CommunityStats {
  totalDiscussions: number;
  totalComments: number;
  totalMembers: number;
  totalActiveMembers: number;
}

const CommunityBoard: React.FC = () => {
  return (
    <Routes>
      <Route path="" element={<CommunityLayout />}>
        <Route index element={<CommunityMainPage />} />
        <Route path="discussion/:discussionId" element={<CommunityDiscussionPage />} />
        <Route path="guidelines" element={<CommunityGuidelinesPage />} />
      </Route>
    </Routes>
  );
};

// Shared layout component
const CommunityLayout: React.FC = () => {
  const { data: communityStats, isLoading: loadingCommunityStats } = useQuery({
    queryKey: ['Community', { component: 'stats' }],
    queryFn: async() => {
      const { data, error } = await supabase.rpc('get_community_stats').single();
      if (error) {
        console.log(error);
      }

      return data as unknown as CommunityStats;
    }
  });

  const [showNewDiscussionModal, setShowNewDiscussionModal] = useState<boolean>(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState<boolean>(false);

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-50">
      {/* Header section */}
      <div className="py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-2/3">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">LSAT Gains Community</h1>
              <p className="text-xl text-indigo-100 mb-6">
                Connect with fellow LSAT students, share strategies, and get advice from those who've been in your shoes.
              </p>
              <div className="flex space-x-4">
                <button 
                  className="px-6 py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors shadow-md flex items-center"
                  onClick={() => setShowNewDiscussionModal(true)}
                >
                  <PlusCircle className="mr-2 h-5 w-5" />
                  Start New Discussion
                </button>
                <button 
                  className="px-6 py-3 bg-indigo-500/30 text-white rounded-lg font-medium hover:bg-inherit/40 transition-colors border border-indigo-300 flex items-center"
                  onClick={() => setShowSubscribeModal(true)}
                >
                  <Bell className="mr-2 h-5 w-5" />
                  Subscribe to Updates
                </button>
              </div>
            </div>
            
            <div className="md:w-1/3 mt-8 md:mt-0">
              <div className="bg-white/10 rounded-xl p-4 border border-indigo-300/30">
                <div className="flex items-center mb-3">
                  <Users className="h-5 w-5 text-indigo-100 mr-2" />
                  <h3 className="text-white font-medium">Community Stats</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-indigo-100 text-sm">Active Members</p>
                    <p className="text-white font-bold text-2xl">{communityStats?.totalMembers ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Discussions</p>
                    <p className="text-white font-bold text-2xl">{communityStats?.totalDiscussions ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Responses</p>
                    <p className="text-white font-bold text-2xl">{communityStats?.totalComments ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Online Now</p>
                    <p className="text-white font-bold text-2xl">{communityStats?.totalActiveMembers ?? 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Child routes will be rendered here */}
      <Outlet />

      {/* Shared Modals */}
      {showNewDiscussionModal && (
        <CommunityModals.NewDiscussionModal 
          showNewDiscussionModal={showNewDiscussionModal}
          setShowNewDiscussionModal={setShowNewDiscussionModal}
        />
      )}
      
      {showSubscribeModal && (
        <CommunityModals.SubscribeModal
          showSubscribeModal={showSubscribeModal}
          setShowSubscribeModal={setShowSubscribeModal}
        />
      )}
    </div>
  );
};

// Main Community Page
const CommunityMainPage: React.FC = () => {
  const { user } = useAuth();
  const { data: session } = useSession();
  const navigate = useNavigate();
  
  // Client states
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);
  const [showSortDropdown, setShowSortDropdown] = useState<boolean>(false);
  const [showVerificationModal, setShowVerificationModal] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedSort, setSelectedSort] = useState<string>('recent');
  
  // Query hooks
  const queryClient = useQueryClient();
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories();
  
  // Create query params based on current filters
  const discussionQueryParams: DiscussionQueryParams = {
    categoryId: activeCategory !== 'all' ? activeCategory : undefined,
    searchQuery: searchQuery || undefined,
    filter: selectedFilter as any,
    sort: selectedSort as any,
    page: currentPage,
    limit: 10
  };
  
  const { 
    data: discussionsData, 
    isLoading: isLoadingDiscussions,
    isError: isErrorDiscussions
  } = useDiscussions(discussionQueryParams);
  
  const { 
    data: popularTopicsData, 
    isLoading: isLoadingPopularTopics 
  } = usePopularDiscussions(5);
  
  // Prepare categories array with icons
  const categories = categoriesData?.map(category => {
    // Map category icon string to React component
    let icon;
    switch (category.icon) {
      case 'message-square':
        icon = <MessageSquare size={18} />;
        break;
      case 'pen-tool':
        icon = <MessageSquare size={18} />;
        break;
      case 'book-open':
        icon = <MessageSquare size={18} />;
        break;
      case 'layers':
        icon = <MessageSquare size={18} />;
        break;
      case 'award':
        icon = <MessageSquare size={18} />;
        break;
      default:
        icon = <MessageSquare size={18} />;
    }
    
    return {
      id: category.id,
      slug: category.slug,
      name: category.name,
      icon
    };
  }) || [];
  
  // Add "All Discussions" category to the beginning
  const allCategories = [
    { id: 'all', slug: 'all', name: 'All Discussions', icon: <MessageSquare size={18} /> },
    ...categories
  ];
  
  // Filter options
  const filterOptions = [
    { id: 'all', name: 'All Discussions' },
    { id: 'pinned', name: 'Pinned Only' },
    { id: 'solved', name: 'Solved Questions' },
    { id: 'unsolved', name: 'Unsolved Questions' },
    { id: 'my-posts', name: 'My Posts' },
    { id: 'my-replies', name: 'My Replies' }
  ];
  
  // Sort options
  const sortOptions = [
    { id: 'recent', name: 'Most Recent' },
    { id: 'popular', name: 'Most Popular' },
    { id: 'views', name: 'Most Views' },
    { id: 'replies', name: 'Most Replies' },
    { id: 'likes', name: 'Most Likes' }
  ];
  
  // Get discussions and total count from the query result
  const discussions = discussionsData?.discussions || [];
  const totalDiscussionsCount = discussionsData?.totalCount || 0;
  const totalPages = Math.ceil(totalDiscussionsCount / 10);
  
  // Get popular topics from query
  const popularTopics = popularTopicsData || [];
  
  // Handle filter selection
  const handleFilterSelect = (filterId: string) => {
    setSelectedFilter(filterId);
    setCurrentPage(1);
    setShowFilterDropdown(false);
  };
  
  // Handle sort selection
  const handleSortSelect = (sortId: string) => {
    setSelectedSort(sortId);
    setCurrentPage(1);
    setShowSortDropdown(false);
  };
  
  // Function to handle discussion click - now navigates using react-router with relative path
  const handleDiscussionClick = (discussionId: string): void => {
    navigate(`discussion/${discussionId}`);
  };
  
  // Function to handle author click
  const handleAuthorClick = (e: React.MouseEvent): void => {
    e.stopPropagation(); // Prevent discussion click
    if (!session) {
      setShowLoginModal(true);
    }
    // If logged in, could navigate to user profile instead
  };
  
  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };
  
  return (
    <>
      <MainView
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showFilterDropdown={showFilterDropdown}
        setShowFilterDropdown={setShowFilterDropdown}
        showSortDropdown={showSortDropdown}
        setShowSortDropdown={setShowSortDropdown}
        filterOptions={filterOptions}
        selectedFilter={selectedFilter}
        handleFilterSelect={handleFilterSelect}
        sortOptions={sortOptions}
        selectedSort={selectedSort}
        handleSortSelect={handleSortSelect}
        isLoadingCategories={isLoadingCategories}
        allCategories={allCategories}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        isLoadingDiscussions={isLoadingDiscussions}
        isErrorDiscussions={isErrorDiscussions}
        discussions={discussions}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handleDiscussionClick={handleDiscussionClick}
        handleAuthorClick={handleAuthorClick}
        setShowNewDiscussionModal={() => navigate('')} // Replace with modal state management
        setCurrentView={() => navigate('guidelines')} // Use router navigation
        isLoadingPopularTopics={isLoadingPopularTopics}
        popularTopics={popularTopics}
        setShowVerificationModal={setShowVerificationModal}
      />
      
      {/* Modals specific to this view */}
      {showLoginModal && (
        <LoginModal
          showLoginModal={showLoginModal}
          setShowLoginModal={setShowLoginModal}
          email={''}
          setEmail={() => {}}
          password={''}
          setPassword={() => {}}
          handleSignIn={() => {}}
          signInMutation={{ isPending: false }}
          setCurrentView={() => {}}
        />
      )}
      
      {showVerificationModal && (
        <VerificationModal
          showVerificationModal={showVerificationModal}
          setShowVerificationModal={setShowVerificationModal}
          session={session}
          setShowLoginModal={setShowLoginModal}
        />
      )}
    </>
  );
};

// Discussion Detail Page
const CommunityDiscussionPage: React.FC = () => {
  const { discussionId } = useParams<{ discussionId: string }>();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [newComment, setNewComment] = useState<string>('');
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showUsernameModal, setShowUsernameModal] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [experienceLevel, setExperienceLevel] = useState<string>('');
  const [studyGoals, setStudyGoals] = useState<string>('');
  const [usernameError, setUsernameError] = useState<string>('');
  const { user } = useAuth();
  
  // Query hooks
  const { data: categoriesData } = useCategories();
  const { 
    data: selectedDiscussion, 
    isLoading: isLoadingDiscussionDetail 
  } = useDiscussionDetail(discussionId || null);
  
  // Mutation hooks
  const addCommentMutation = useAddComment();
  const toggleLikeMutation = useToggleLike();
  const toggleBookmarkMutation = useToggleBookmark();
  const updateDiscussionMutation = useUpdateDiscussion();

  // Prepare categories array with icons (simplified version)
  const allCategories = categoriesData?.map(category => ({
    id: category.id,
    slug: category.slug,
    name: category.name,
    icon: <MessageSquare size={18} />
  })) || [];
  
  // Handle like toggle
  const handleLikeToggle = (type: 'Discussion' | 'Comment', id: string) => {
    if (!session) {
      setShowLoginModal(true);
      return;
    }
    
    toggleLikeMutation.mutate({ type, id });
  };
  
  // Handle bookmark toggle
  const handleBookmarkToggle = (discussionId: string) => {
    if (!session) {
      setShowLoginModal(true);
      return;
    }
    
    toggleBookmarkMutation.mutate({ discussionId });
  };
  
  // Handle comment submission
  const handleCommentSubmit = (discussionId: string) => {
    if (!session) {
      setShowLoginModal(true);
      return;
    }
    
    if (!newComment.trim()) return;
    
    addCommentMutation.mutate(
      { content: newComment, discussionId },
      {
        onSuccess: () => {
          setNewComment('');
          setUsernameError('');
        },
        onError: (error) => {
          if (error === errNoUsername) {
            setShowUsernameModal(true);
          }
        },
      }
    );
  };
  
  // Function to handle author click
  const handleAuthorClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!session) {
      setShowLoginModal(true);
    }
  };
  
  // Handle sign up for username
  const handleSignUp = async () => {
    setUsernameError('');

    if (!username || !experienceLevel) {
      setShowUsernameModal(false);
      return;
    }
    
    const { error } = await supabase.from('profiles').update({
      username,
      experience_level: experienceLevel,
      study_goals: studyGoals,
    }).eq('id', user?.id ?? '');

    if (error) {
      if (error.message?.includes(`violates check constraint "profiles_username_check"`)) {
        setUsernameError('Username does not meet the requirements.');
        return;
      }

      const err = error.message.includes(`duplicate key value violates unique constraint`) ?
        `Username has already been taken.` : error.message;

      setUsernameError(`${err}`);
      return;    
    }

    setShowUsernameModal(false);
    setUsername('');
    setExperienceLevel('');
    setStudyGoals('');
  };
  
  return (
    <>
      <DiscussionDetailView
        selectedDiscussion={selectedDiscussion}
        isLoadingDiscussionDetail={isLoadingDiscussionDetail}
        allCategories={allCategories}
        newComment={newComment}
        setNewComment={setNewComment}
        setCurrentView={() => navigate('../')} // Use router navigation to go back
        handleLikeToggle={handleLikeToggle}
        handleBookmarkToggle={handleBookmarkToggle}
        handleCommentSubmit={handleCommentSubmit}
        handleAuthorClick={handleAuthorClick}
        session={session}
        setShowLoginModal={setShowLoginModal}
        addCommentMutation={addCommentMutation}
        updateDiscussionMutation={updateDiscussionMutation}
      />
      
      {/* Modals specific to this view */}
      {showLoginModal && (
        <LoginModal
          showLoginModal={showLoginModal}
          setShowLoginModal={setShowLoginModal}
          email={''}
          setEmail={() => {}}
          password={''}
          setPassword={() => {}}
          handleSignIn={() => {}}
          signInMutation={{ isPending: false }}
          setCurrentView={() => {}}
        />
      )}
      
      {showUsernameModal && (
        <UsernameScreen
          username={username}
          setUsername={setUsername}
          experienceLevel={experienceLevel}
          setExperienceLevel={setExperienceLevel}
          studyGoals={studyGoals}
          setStudyGoals={setStudyGoals}
          handleSignUp={handleSignUp}
          usernameError={usernameError}
        />
      )}
    </>
  );
};

// Guidelines Page
const CommunityGuidelinesPage: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <GuidelinesView setCurrentView={() => navigate('../')} />
  );
};

// Group all modals into one namespace for organization
const CommunityModals = {
  NewDiscussionModal: ({ showNewDiscussionModal, setShowNewDiscussionModal }: any) => {
    const { data: session } = useSession();
    const navigate = useNavigate();
    const [newDiscussionTitle, setNewDiscussionTitle] = useState<string>('');
    const [newDiscussionContent, setNewDiscussionContent] = useState<string>('');
    const [newDiscussionCategory, setNewDiscussionCategory] = useState<string>('');
    const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
    const [showUsernameModal, setShowUsernameModal] = useState<boolean>(false);
    const { data: categoriesData } = useCategories();
    const createDiscussionMutation = useCreateDiscussion();
    
    // Prepare categories array
    const allCategories = categoriesData?.map(category => ({
      id: category.id,
      slug: category.slug,
      name: category.name,
      icon: <MessageSquare size={18} />
    })) || [];
    
    // Handle discussion creation
    const handleCreateDiscussion = () => {
      if (!session) {
        setShowLoginModal(true);
        return;
      }
      
      if (!newDiscussionTitle.trim() || !newDiscussionContent.trim() || !newDiscussionCategory) {
        return;
      }
      
      createDiscussionMutation.mutate(
        {
          title: newDiscussionTitle,
          content: newDiscussionContent,
          categoryId: newDiscussionCategory
        },
        {
          onSuccess: (data) => {
            setShowNewDiscussionModal(false);
            setNewDiscussionTitle('');
            setNewDiscussionContent('');
            setNewDiscussionCategory('');
            navigate(`../discussion/${data.id}`);
          },
          onError: (error) => {
            if (error === errNoUsername) {
              setShowUsernameModal(true);
            }
          },
        }
      );
    };
    
    return (
      <NewDiscussionModal
        showNewDiscussionModal={showNewDiscussionModal}
        setShowNewDiscussionModal={setShowNewDiscussionModal}
        newDiscussionTitle={newDiscussionTitle}
        setNewDiscussionTitle={setNewDiscussionTitle}
        newDiscussionCategory={newDiscussionCategory}
        setNewDiscussionCategory={setNewDiscussionCategory}
        newDiscussionContent={newDiscussionContent}
        setNewDiscussionContent={setNewDiscussionContent}
        allCategories={allCategories}
        handleCreateDiscussion={handleCreateDiscussion}
        session={session}
        setShowLoginModal={setShowLoginModal}
        createDiscussionMutation={createDiscussionMutation}
      />
    );
  },
  
  SubscribeModal: ({ showSubscribeModal, setShowSubscribeModal }: any) => {
    const { data: session } = useSession();
    const [email, setEmail] = useState<string>('');
    const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
    
    return (
      <SubscribeModal
        showSubscribeModal={showSubscribeModal}
        setShowSubscribeModal={setShowSubscribeModal}
        email={email}
        setEmail={setEmail}
        session={session}
        setShowLoginModal={setShowLoginModal}
      />
    );
  }
};

export default CommunityBoard;
